/**
 * AuthContext - Google OAuth + Session Management
 * ================================================
 * SESSION 109: SaaS Auth with Highlander Rule
 * 
 * Features:
 * - Google OAuth login flow (server-side redirect)
 * - JWT storage with role-based access
 * - Time-Bomb: auto-logout at exact subscription expiry
 * - Visibility re-check: when tab becomes active, verify expiry
 * - 401 interceptor: session eviction / expired token → forced logout
 * - Highlander Rule: login on Device B evicts Device A
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

const AuthContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL || '';

// Decode JWT payload without verification (client-side)
function decodeJWT(token) {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginError, setLoginError] = useState(null);
  const timeBombRef = useRef(null);

  // ========================================
  // LOGOUT — Clears all state
  // ========================================
  const logout = useCallback((reason = null) => {
    // Clear time-bomb
    if (timeBombRef.current) {
      clearTimeout(timeBombRef.current);
      timeBombRef.current = null;
    }

    // Call backend logout (best-effort, don't await)
    const currentToken = localStorage.getItem('token');
    if (currentToken) {
      fetch(`${API}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${currentToken}` }
      }).catch(() => {});
    }

    // Clear local state
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setToken(null);

    // Show reason toast
    if (reason === 'SESSION_EVICTED') {
      toast.error('Logged in from another device. This session has been terminated.', { duration: 6000 });
    } else if (reason === 'SUBSCRIPTION_EXPIRED') {
      toast.error('Your subscription has expired. Please contact admin.', { duration: 6000 });
    } else if (reason === 'TOKEN_EXPIRED') {
      toast.info('Session expired. Please sign in again.', { duration: 4000 });
    } else if (reason) {
      toast.error(reason, { duration: 5000 });
    }
  }, []);

  // ========================================
  // TIME-BOMB: Auto-logout at exact expiry
  // ========================================
  const setTimeBomb = useCallback((tokenPayload) => {
    if (timeBombRef.current) {
      clearTimeout(timeBombRef.current);
    }

    const exp = tokenPayload?.exp;
    if (!exp) return;

    const msUntilExpiry = (exp * 1000) - Date.now();
    
    if (msUntilExpiry <= 0) {
      logout('TOKEN_EXPIRED');
      return;
    }

    // Set timeout for auto-logout (cap at 24h to avoid overflow)
    const maxTimeout = 24 * 60 * 60 * 1000;
    const timeout = Math.min(msUntilExpiry, maxTimeout);
    
    timeBombRef.current = setTimeout(() => {
      logout('TOKEN_EXPIRED');
    }, timeout);
  }, [logout]);

  // ========================================
  // VISIBILITY RE-CHECK: When tab becomes active
  // ========================================
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && token) {
        const payload = decodeJWT(token);
        if (payload?.exp && payload.exp * 1000 < Date.now()) {
          logout('TOKEN_EXPIRED');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [token, logout]);

  // ========================================
  // SESSION 109: WebSocket force_logout handler
  // ========================================
  useEffect(() => {
    const handleForceLogout = (event) => {
      const { reason, message } = event.detail || {};
      console.warn('🔐 Force logout event:', reason, message);
      logout(reason || 'SESSION_EVICTED');
    };

    window.addEventListener('force_logout', handleForceLogout);
    return () => window.removeEventListener('force_logout', handleForceLogout);
  }, [logout]);

  // ========================================
  // API FETCH WRAPPER with 401 interceptor
  // ========================================
  const authFetch = useCallback(async (url, options = {}) => {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) {
      throw new Error('No authentication token');
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${currentToken}`,
      },
    });

    if (response.status === 401) {
      const data = await response.json().catch(() => ({}));
      const detail = data.detail || 'Unauthorized';
      
      if (detail === 'SESSION_EVICTED') {
        logout('SESSION_EVICTED');
      } else if (detail === 'SUBSCRIPTION_EXPIRED') {
        logout('SUBSCRIPTION_EXPIRED');
      } else {
        logout('TOKEN_EXPIRED');
      }
      throw new Error(detail);
    }

    return response;
  }, [logout]);

  // ========================================
  // LOGIN via token (from Google OAuth callback)
  // ========================================
  const loginWithToken = useCallback(async (jwt) => {
    const payload = decodeJWT(jwt);
    if (!payload) {
      setLoginError('Invalid token received');
      return false;
    }

    // Check if token is already expired
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      setLoginError('Token has expired');
      return false;
    }

    // Store token first
    localStorage.setItem('token', jwt);
    setToken(jwt);

    // Fetch full user profile SYNCHRONOUSLY before returning
    // This ensures is_active and subscription_end_date are available for ProtectedRoute
    try {
      const response = await fetch(`${API}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${jwt}` }
      });
      
      if (response.ok) {
        const profile = await response.json();
        localStorage.setItem('user', JSON.stringify(profile));
        setUser(profile);
      } else {
        // Fallback to JWT payload if profile fetch fails
        const userData = {
          id: payload.sub,
          email: payload.email,
          role: payload.role || 'user',
          session_id: payload.session_id,
        };
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
      }
    } catch {
      // Fallback to JWT payload if profile fetch fails
      const userData = {
        id: payload.sub,
        email: payload.email,
        role: payload.role || 'user',
        session_id: payload.session_id,
      };
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
    }

    setLoginError(null);

    // Set time-bomb
    setTimeBomb(payload);

    return true;
  }, [setTimeBomb]);

  // ========================================
  // LEGACY LOGIN - REMOVED IN SESSION 109
  // ========================================
  // Password-based login has been removed.
  // All authentication now uses Google OAuth.
  const login = useCallback(async () => {
    throw new Error('Password login has been removed. Please use Google Sign In.');
  }, []);

  // ========================================
  // INIT: Restore session from localStorage
  // ========================================
  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (savedToken && savedUser) {
      const payload = decodeJWT(savedToken);
      
      // Check expiry
      if (payload?.exp && payload.exp * 1000 < Date.now()) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setLoading(false);
        return;
      }

      try {
        setUser(JSON.parse(savedUser));
        setToken(savedToken);
        setTimeBomb(payload);
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }

    setLoading(false);
  }, [setTimeBomb]);

  // ========================================
  // GOOGLE LOGIN: Redirect to backend OAuth
  // ========================================
  const loginWithGoogle = useCallback(() => {
    // SESSION 135: Pass invite code if present (from /invite/CODE route)
    const inviteCode = localStorage.getItem('invite_code');
    if (inviteCode) {
      window.location.href = `${API}/api/auth/google?invite=${inviteCode}`;
    } else {
      window.location.href = `${API}/api/auth/google`;
    }
  }, []);

  // ========================================
  // CONTEXT VALUE
  // ========================================
  const isAdmin = user?.role === 'admin';
  const isAuthenticated = !!token && !!user;

  const value = {
    user,
    token,
    loading,
    loginError,
    isAdmin,
    isAuthenticated,
    login,              // Legacy password login
    loginWithGoogle,    // Google OAuth redirect
    loginWithToken,     // Token-based login (from callback)
    logout,
    authFetch,          // Fetch wrapper with 401 interceptor
    setLoginError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// ========================================
// PROTECTED ROUTE COMPONENT
// ========================================
export const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-primary)' }}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login
    window.location.href = '/login';
    return null;
  }

  // Check if user is active (for non-admin)
  if (user?.role !== 'admin') {
    const isActive = user?.is_active;
    const subEnd = user?.subscription_end_date;
    
    if (!isActive || (subEnd && new Date(subEnd) < new Date())) {
      window.location.href = '/holding';
      return null;
    }
  }

  if (requireAdmin && user?.role !== 'admin') {
    // Redirect non-admin users to dashboard
    window.location.href = '/';
    return null;
  }

  return children;
};

export default AuthProvider;
